import EmberService, { service } from '@ember/service';

import type { APIData, PopupData, RequestError, Service } from 'webapp-types';
import { ProductIssueType, type ReportIssueSenderType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import CommonPopupsPopupReportIssueComponent from 'webapp/components/common/popups/popup-report-issue';
import CustomMetricsEditPopupComponent from 'webapp/components/custom-metrics/edit/popup';
import DataPerformancePopupDataPerformanceSummaryComponent from 'webapp/components/data-performance/popup-summary';
import DataSelectEditPopupComponent from 'webapp/components/data-select/edit/popup';

export interface EventIssueData {
	type: ProductIssueType;
	page: string;
	url: string;
	errorMessage: string;
	feature?: string;
	endpoint?: string;
	httpStatusCode?: RequestError['code'];
	duration?: number;
	requestId?: string;
	errorItemId?: string; // blockId for now
}

export default class IssuesReportService extends EmberService {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;

	triggerEventIssue(eventData: EventIssueData): void {
		if (this.requestService.isAbort(eventData.httpStatusCode!, eventData.errorMessage)) {
			return;
		}
		const trackingData = this.getEventIssueTrackingData(eventData);
		this.analyticsService.event(TrackingEvent.ISSUE, trackingData);
	}

	triggerEventIssueReported(eventData: EventIssueData): void {
		const trackingData = this.getEventIssueTrackingData(eventData);
		this.analyticsService.event(TrackingEvent.ISSUE_REPORTED, trackingData);
	}

	showInfolayerError(
		text: string,
		data: {
			endpoint: string;
			httpStatusCode: RequestError['code'];
			errorText: string;
			requestId?: string;
			eventIssueData: EventIssueData;
			senderType?: ReportIssueSenderType;
		},
	) {
		const { httpStatusCode, errorText } = data;
		const isTimeout = this.requestService.isTimeout(httpStatusCode, errorText);

		if (
			this.currentUserService.isLoggedIn &&
			((typeof httpStatusCode === 'number' && httpStatusCode >= 500) || isTimeout)
		) {
			this.infolayerService.errorWithActionButton(text, {
				text: 'Report issue',
				onClick: () => {
					this.openReportIssuePopup({
						endpoint: data.endpoint,
						httpStatusCode: data.httpStatusCode,
						requestId: data.requestId,
						senderType: data.senderType,
						eventIssueData: data.eventIssueData,
						isTimeout,
					});
				},
			});
		} else {
			this.infolayerService.error(text);
		}
	}

	private getEventIssueTrackingData(trackData: EventIssueData) {
		// data passed from outside
		const {
			type,
			endpoint = '',
			httpStatusCode = 200,
			errorMessage,
			duration,
			feature,
			requestId,
		} = trackData;

		// data to be modified
		const data = { ...trackData };

		// set default values
		if (!trackData.endpoint) {
			data.endpoint = endpoint;
		}

		if (!trackData.httpStatusCode) {
			data.httpStatusCode = httpStatusCode;
		}

		if (!trackData.feature) {
			data.feature = feature;
		}

		if (!trackData.requestId) {
			data.requestId = requestId;
		}

		const isTimeout =
			this.requestService.isTimeout(httpStatusCode, errorMessage) ||
			type === ProductIssueType.DATA_LOADING_DELAY;
		if (isTimeout) {
			data.duration = duration;
		} else {
			data.duration = 0;
		}

		// generate type for some exceptions
		if (
			![ProductIssueType.DATA_WARNING, ProductIssueType.DATA_LOADING_DELAY].includes(
				data.type,
			)
		) {
			if (
				endpoint.includes('preview') &&
				(endpoint.includes('customqueries') || endpoint.includes('scripts'))
			) {
				data.type = isTimeout
					? ProductIssueType.DATA_PREVIEW_TIMEOUT
					: ProductIssueType.DATA_PREVIEW_ERROR;
			} else if (endpoint.includes('dataquery')) {
				data.type = isTimeout
					? ProductIssueType.DATA_QUERY_TIMEOUT
					: ProductIssueType.DATA_QUERY_ERROR;
			} else if (isTimeout) {
				data.type = ProductIssueType.GENERAL_TIMEOUT;
			}
		}

		// track feature according to active popup
		const activePopup = this.popupService.activePopup;
		if (activePopup) {
			switch (activePopup.componentInnerClass) {
				case DataSelectEditPopupComponent:
					data.feature = 'advanced_popup';
					break;
				case DataPerformancePopupDataPerformanceSummaryComponent:
					data.feature = 'performance_summary';
					break;
				case CustomMetricsEditPopupComponent: {
					const contentCt = activePopup.component?.popupNode?.querySelector('.content');
					if (contentCt) {
						if (contentCt.classList.contains('content--script')) {
							data.feature = 'data_calculation';
						} else if (contentCt.classList.contains('content--custom-query')) {
							data.feature = 'metric_builder';
						}
					}
					break;
				}
			}
		}

		// track feature according to endpoint
		const activatedRoute = this.navigationService.activatedRoute;
		if (!data.feature) {
			if (
				endpoint.includes('metrics') ||
				endpoint.includes('metrickeys') ||
				endpoint.includes('metrics')
			) {
				data.feature = 'metrics';
			} else if (endpoint.includes('customqueries')) {
				data.feature = 'metric_builder';
			} else if (endpoint.includes('boards')) {
				data.feature = 'databoards';

				if (endpoint.includes('shares')) {
					data.feature = 'databoard_snapshots';
				}
			} else if (endpoint.includes('board-loops')) {
				data.feature = 'databoard_loops';
			} else if (endpoint.includes('report')) {
				data.feature = 'reports';
			} else if (endpoint.includes('report-schedules')) {
				data.feature = 'report_snapshots';
			} else if (endpoint.includes('goals')) {
				data.feature = 'goals';
			} else if (endpoint.includes('scorecards')) {
				data.feature = 'scorecards';

				if (
					activatedRoute.includes('notifications.digests') ||
					activatedRoute.includes('metrics.details')
				) {
					data.feature = 'metric_digest';
				}
			} else if (endpoint.includes('alert-settings')) {
				data.feature = 'alerts';
			} else if (endpoint.includes('forecasts')) {
				data.feature = 'forecasts';
			} else if (endpoint.includes('activity-logs')) {
				data.feature = 'activity_log';
			} else if (
				endpoint.includes('sources') ||
				endpoint.includes('datasource-types') ||
				endpoint.includes('connect')
			) {
				data.feature = 'data_sources';
			} else if (endpoint.includes('templates')) {
				data.feature = 'templates';
			}
		}

		// track feature according to active route
		if (!data.feature) {
			if (activatedRoute.includes('designer')) {
				data.feature = 'designer';
			} else if (
				activatedRoute.includes('metrics') ||
				activatedRoute.includes('metric-library')
			) {
				data.feature = 'metrics';

				if (activatedRoute.includes('querybuilder')) {
					data.feature = 'metric_builder';
				} else if (activatedRoute.includes('scripts')) {
					data.feature = 'data_calculation';
				}
			} else if (activatedRoute.includes('boards')) {
				data.feature = 'databoards';

				if (activatedRoute.includes('loops')) {
					data.feature = 'databoard_loops';
				}
			} else if (activatedRoute.includes('reports')) {
				data.feature = 'reports';
			} else if (activatedRoute.includes('datamanager')) {
				data.feature = 'data_sources';
			} else if (activatedRoute.includes('goals')) {
				data.feature = 'goals';
			} else if (activatedRoute.includes('scorecards')) {
				data.feature = 'scorecards';
			} else if (activatedRoute.includes('snapshots')) {
				data.feature = 'databoard_snapshots';
			} else if (activatedRoute.includes('alerts')) {
				data.feature = 'alerts';
			} else if (activatedRoute.includes('forecasts')) {
				data.feature = 'forecasts';
			} else if (activatedRoute.includes('clients')) {
				data.feature = 'clients';
			}
		}

		// if no feature is set
		if (!data.feature) {
			data.feature = 'other';
		}

		const errorMessageData = this.formatErrorMessage(data.errorMessage);

		const eventData: APIData.Tracking.Data = {
			type: data.type,
			page: data.page,
			url: data.url,
			error_message: errorMessageData.message,
			error_item_id: trackData.errorItemId || errorMessageData.errorItemId,
			feature: data.feature,
			endpoint: this.normalizeEndpoint(data.endpoint),
			http_status_code: data.httpStatusCode,
			duration: data.duration,
		};

		if (data.requestId) {
			eventData.request_id = data.requestId;
		}

		return eventData;
	}

	private openReportIssuePopup(data: PopupData.Common.ReportIssue) {
		this.popupService
			.createModal(CommonPopupsPopupReportIssueComponent, {
				popupClass: 'popup-type-report-issue',
			})
			.open(data);
	}

	private formatErrorMessage(originalMessage: string): { message: string; errorItemId?: string } {
		// Example: [API-70U4IV] <b>Sorry, but there seems to be an internal issue.</b> Please attempt your action again. In case this error is persisting, report it to our technical support.

		// Regex pattern to match [API-XXXXXX] where XXXXXX are dynamic characters.
		// Also there is white space at the end
		const regex = /\[API-[A-Za-z0-9]+\]\s?/g;

		let message = originalMessage.replace(regex, '');
		let errorItemId: string | undefined;

		// Replace space access IDs with placeholder
		// Example: "Space access 1234 does not exist" -> "Space access {space_access_id} does not exist"
		const spaceAccessRegex = /Space access \d+/gi;
		const spaceAccessId = message.match(spaceAccessRegex)?.[0];

		message = message.replace(spaceAccessRegex, 'Space access {space_access_id}');

		if (spaceAccessId) {
			errorItemId = spaceAccessId;
		}

		return {
			message,
			errorItemId,
		};
	}

	private normalizeEndpoint(endpoint?: string) {
		if (!endpoint) {
			return undefined;
		}

		// Trim everything after "?"
		let normalized = endpoint.split('?')[0]!;

		// Replace specific path patterns with placeholders
		// /d/token/<whatever>/.. -> /d/token/{token}
		normalized = normalized.replace(/\/d\/token\/[^/]+/, '/d/token/{token}');

		// /d/previews/<whatever>/.. -> /d/preview/{hash}
		normalized = normalized.replace(/\/d\/previews\/[^/]+/, '/d/preview/{hash}');

		// /d/datawalls/<whatever>/.. -> /d/datawalls/{hash}
		normalized = normalized.replace(/\/d\/datawalls\/[^/]+/, '/d/datawalls/{hash}');

		// /d/metrics/<metric_id> -> /d/metrics/{id}
		normalized = normalized.replace(/\/d\/metrics\/[^/]+/, '/d/metrics/{id}');

		// Replace /<numbers> with /{id} (standalone numeric path segments, including negative)
		normalized = normalized.replace(/\/-?\d+/g, '/{id}');

		return normalized;
	}
}

declare module '@ember/service' {
	interface Registry {
		'issues-report': IssuesReportService;
	}
}
